/**
 * @fileoverview Popover form for editing an indirect cost entry, anchored to the edit button.
 * @module features/indirect-costs/components/IndirectCostEditPopover
 */

import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import RangePickerPopover from '@/shared/components/ui/RangePickerPopover'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { Trash2 } from 'lucide-react'
import { CostInput, parseCostToNumber } from '@/shared/components/ui'
import {
  type IndirectCostEntryDTO,
  type IndirectCostSubcategoryDTO,
  type PageIndirectCostResponse,
} from '../schemas/indirectCostsSchema'
import { addDays, formatISO } from 'date-fns'
import { updateIndirectCost } from '../api/indirectCostsService'
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useIndirectCostSubcategories } from '../hooks/useIndirectCostSubcategories'
import { useLinkedPaymentsCount } from '../hooks/useLinkedPaymentsCount'
import IndirectCostSubcategorySelect from './IndirectCostSubcategorySelect'

export interface IndirectCostEditPopoverProps {
  /** Entry to edit */
  row: IndirectCostEntryDTO
  /** Called after successful update to refresh data */
  onUpdated?: (() => void) | undefined
  /** Callback to trigger deletion */
  onDelete?: (row: IndirectCostEntryDTO) => void
  /** Optional custom trigger content (icon/button). */
  children?: ReactNode
}

/**
 * Edit popover with prefilled values and consistent date semantics:
 * - Backend stores `effectiveTo` as exclusive, UI shows inclusive (to − 1 day)
 * - Custom range sends (inclusiveTo + 1 day) to API
 * - If the existing period is a full month, the Month preset is selected by default
 *
 * @component
 */
export default function IndirectCostEditPopover({
  row,
  onUpdated,
  onDelete,
  children,
}: IndirectCostEditPopoverProps): ReactElement {
  const qc = useQueryClient()
  const { t } = useTranslation('features-indirect-costs')
  const [open, setOpen] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const startDate = useMemo(() => new Date(row.effectiveFrom), [row.effectiveFrom])
  const inclusiveEnd = useMemo(() => addDays(new Date(row.effectiveTo), -1), [row.effectiveTo])

  const monthLastDay = useMemo(
    () => new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0),
    [startDate]
  )
  const isFullMonth =
    startDate.getDate() === 1 &&
    inclusiveEnd.getFullYear() === monthLastDay.getFullYear() &&
    inclusiveEnd.getMonth() === monthLastDay.getMonth() &&
    inclusiveEnd.getDate() === monthLastDay.getDate()

  const [amount, setAmount] = useState<string>(String(row.reportingAmount))
  const [currency, setCurrency] = useState<string>(row.reportingCurrency)
  const [useCustomRange, setUseCustomRange] = useState<boolean>(!isFullMonth)
  const [customRange, setCustomRange] = useState<Date[] | null>(
    !isFullMonth ? [startDate, inclusiveEnd] : null
  )
  const rowSubcategory = useMemo<IndirectCostSubcategoryDTO>(
    () => ({
      id: -1,
      uuid: row.subcategoryUuid,
      displayName: row.subcategoryDisplayName,
      slug: row.subcategorySlug,
      category: row.category,
      system: row.subcategorySystem,
      autoCreateMonthly: false,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subcategorySlug not used in the memo value
    [
      row.category,
      row.createdAt,
      row.subcategoryDisplayName,
      row.subcategorySystem,
      row.subcategoryUuid,
      row.updatedAt,
    ]
  )
  const {
    data: fetchedSubcategories = [],
    isLoading: isLoadingSubcategories,
    isError: isSubcategoryError,
    error: subcategoryError,
  } = useIndirectCostSubcategories()

  const { counts: linkedPaymentsCounts } = useLinkedPaymentsCount([row.uuid])
  const hasLinkedPayments = linkedPaymentsCounts[row.uuid]?.hasLinkedPayments ?? false
  const mergedSubcategories = useMemo(() => {
    if (fetchedSubcategories.some(item => item.uuid === rowSubcategory.uuid)) {
      return fetchedSubcategories
    }
    return [...fetchedSubcategories, rowSubcategory]
  }, [fetchedSubcategories, rowSubcategory])
  const [selectedSubcategoryUuid, setSelectedSubcategoryUuid] = useState<string | null>(
    row.subcategoryUuid
  )

  const monthLabel = useMemo(
    () =>
      new Date(startDate.getFullYear(), startDate.getMonth()).toLocaleString(undefined, {
        month: 'short',
        year: 'numeric',
      }),
    [startDate]
  )

  useEffect(() => {
    if (!open) {
      setSelectedSubcategoryUuid(row.subcategoryUuid)
    }
  }, [open, row.subcategoryUuid])

  const defaultSubcategory = useMemo(() => {
    const filteredSubcategories = mergedSubcategories.filter(item => !item.system);

    if (selectedSubcategoryUuid) {
      const match = filteredSubcategories.find(item => item.uuid === selectedSubcategoryUuid)
      if (match) return match
    }
    const rowMatch = filteredSubcategories.find(item => item.uuid === row.subcategoryUuid)
    if (rowMatch) return rowMatch
    const preferredSystem = filteredSubcategories.find(
      item => item.system && item.category === row.category
    )
    if (preferredSystem) return preferredSystem
    const anySystem = filteredSubcategories.find(item => item.system)
    if (anySystem) return anySystem
    return filteredSubcategories.at(0) ?? null
  }, [mergedSubcategories, row.category, row.subcategoryUuid, selectedSubcategoryUuid])

  useEffect(() => {
    if (
      selectedSubcategoryUuid &&
      !mergedSubcategories.some(item => item.uuid === selectedSubcategoryUuid) &&
      defaultSubcategory
    ) {
      setSelectedSubcategoryUuid(defaultSubcategory.uuid)
      return
    }
    if (!selectedSubcategoryUuid && defaultSubcategory) {
      setSelectedSubcategoryUuid(defaultSubcategory.uuid)
    }
  }, [defaultSubcategory, mergedSubcategories, selectedSubcategoryUuid])

  const subcategoryErrorMessage =
    isSubcategoryError && subcategoryError instanceof Error
      ? subcategoryError.message
      : isSubcategoryError
        ? t('subcategorySelect.error', { ns: 'features-indirect-costs' })
        : undefined

  const isInvalid = useMemo(() => {
    const v = Number.parseFloat(amount)
    return !Number.isFinite(v) || v <= 0
  }, [amount])

  const mutation = useMutation<IndirectCostEntryDTO, Error, void>({
    mutationFn: async (): Promise<IndirectCostEntryDTO> => {
      const [from, to] =
        useCustomRange && customRange && customRange.length === 2
          ? [customRange[0] as Date, customRange[1] as Date]
          : [new Date(startDate), new Date(monthLastDay)]
      // Use local calendar date to avoid UTC shifting issues
      const pad = (d: Date): string => formatISO(d, { representation: 'date' })
      const effectiveFrom = pad(from)
      // Month preset: convert inclusive monthLastDay -> exclusive 1st of next month
      const effectiveTo = useCustomRange
        ? pad(addDays(to, 1))
        : pad(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1))

      return updateIndirectCost(row.uuid, {
        originalAmount: parseCostToNumber(amount) ?? 0,
        originalCurrency: currency,
        effectiveFrom,
        effectiveTo,
        subcategoryUuid: selectedSubcategoryUuid as string,
      })
    },
    onSuccess: async data => {
      toast.success(t('toasts.updated'))
      setOpen(false)

      // Update cache optimistically with the updated data
      qc.setQueryData<InfiniteData<PageIndirectCostResponse>>(['indirect-costs'], oldData => {
        if (!oldData) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page: PageIndirectCostResponse) => ({
            ...page,
            content: page.content.map((item: IndirectCostEntryDTO) =>
              item.uuid === data.uuid ? data : item
            ),
          })),
        }
      })

      setSelectedSubcategoryUuid(data.subcategoryUuid)
      onUpdated?.()
    },
    onError: (err: unknown) => {
      // Check for 409 conflict (linked payments)
      const isLinkedPaymentsError =
        err instanceof Error &&
        (err.message.includes('linked to payments') ||
          ('response' in err &&
            (err as { response?: { status?: number } }).response?.status === 409))
      if (isLinkedPaymentsError) {
        toast.error(t('toasts.linkedPaymentsError'))
        return
      }
      toast.error(err instanceof Error ? err.message : t('toasts.updateFailed'))
    },
  })

  const disableSave =
    isInvalid ||
    mutation.isPending ||
    !selectedSubcategoryUuid ||
    isLoadingSubcategories ||
    isSubcategoryError

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {children ?? (
            <button aria-label={t('aria.edit')} className="glass-button rounded-md p-2" type="button">
              <span className="sr-only">{t('aria.edit')}</span>
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[560px] p-5 rounded-lg shadow-lg" align="end" sideOffset={8}>
          <div className="text-xl font-semibold mb-3">{t('edit.title')}</div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-4">
            <label className="text-sm text-muted-foreground col-span-1">
              {t('edit.labels.subcategory')}
            </label>
            <div className="col-span-2">
              <IndirectCostSubcategorySelect
                value={selectedSubcategoryUuid}
                onChange={setSelectedSubcategoryUuid}
                subcategories={mergedSubcategories}
                isLoading={isLoadingSubcategories}
                isError={isSubcategoryError}
                {...(subcategoryErrorMessage ? { errorMessage: subcategoryErrorMessage } : {})}
                placeholder={t('subcategorySelect.placeholder')}
                emptyLabel={t('subcategorySelect.empty')}
                loadingLabel={t('subcategorySelect.loading')}
                disabled={isSubcategoryError}
              />
              {!selectedSubcategoryUuid && !isLoadingSubcategories ? (
                <p className="text-xs text-destructive mt-1">{t('validation.subcategoryRequired')}</p>
              ) : null}
            </div>

            <label className="text-sm text-muted-foreground col-span-1">
              {t('edit.labels.amount')}
            </label>
            <div className="col-span-2">
              <CostInput
                costValue={amount}
                onCostChange={setAmount}
                currencyValue={currency}
                onCurrencyChange={setCurrency}
                hideLabel
                disabled={hasLinkedPayments}
              />
              {hasLinkedPayments && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('edit.amountLockedHint')}
                </p>
              )}
            </div>

            <label className="text-sm text-muted-foreground col-span-1">
              {t('edit.labels.applicablePeriod')}
            </label>
            <div className="col-span-2">
              <div className="flex gap-3">
                <button
                  type="button"
                  className={`px-3 py-2 rounded-md border ${!useCustomRange ? 'bg-accent/40 border-border' : 'border-border'}`}
                  onClick={() => setUseCustomRange(false)}
                >
                  {monthLabel}
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 rounded-md border ${useCustomRange ? 'bg-accent/40 border-border' : 'border-border'}`}
                  onClick={() => setUseCustomRange(true)}
                >
                  {t('actions.custom')}
                </button>
              </div>
              {useCustomRange && (
                <div className="mt-3">
                  <RangePickerPopover
                    value={{
                      from: customRange?.[0] as Date | undefined,
                      to: customRange?.[1] as Date | undefined,
                    }}
                    onChange={(from, to) => setCustomRange([from, to])}
                    align="start"
                  />
                </div>
              )}
            </div>

            <div className="col-span-3 flex items-center justify-between mt-2">
              {onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 px-2"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('actions.delete')}
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t('actions.cancel')}
                </Button>
              )}
              <Button
                type="button"
                variant="default"
                disabled={disableSave}
                onClick={() => void mutation.mutate()}
              >
                {mutation.isPending ? t('actions.saving') : t('actions.save')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('deleteConfirm.title', { subcategory: row.subcategoryDisplayName })}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <button type="button" className="glass-button rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
                {t('actions.cancel')}
                <Kbd>Esc</Kbd>
              </button>
            </DialogClose>
            <button
              type="button"
              className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 flex items-center gap-2"
              onClick={() => {
                setShowDeleteDialog(false)
                setOpen(false)
                onDelete?.(row)
              }}
              autoFocus
            >
              {t('actions.delete')}
              <Kbd className="bg-destructive-foreground/10 text-destructive-foreground/70">↵</Kbd>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
